import { Divider, Headline, Layout, Text } from '@oneaudi/unified-web-components';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';

export function PriceDetailsAsBuiltDialog({ open, onClose }: DialogProps) {
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<PriceDetailsAsBuiltDialogContent />
		</Dialog>
	);
}

function PriceDetailsAsBuiltDialogContent() {
	const { catalog } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();

	const { priceDateRemark } = usePriceDetailRemarks();

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const priceParts = getFilteredPriceParts(priceSummary, config);

	return (
		<div className="flex flex-col gap-8">
			<div className="flex flex-col gap-2">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'bold' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
				</Headline>
			</div>

			<div className="flex flex-col">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			<ul className="flex flex-col gap-6">
				{priceParts?.map((part) => (
					<PriceSection
						key={part.type}
						pricePartType={part.type}
						title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
						price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
					/>
				))}
			</ul>

			<Divider />

			<PriceSection
				pricePartType="total"
				title={i18n('ngc.priceDetails.priceAsBuilt')}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
			/>

			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
		</div>
	);
}

function PriceSection({
	pricePartType,
	title,
	price,
	bold,
}: {
	pricePartType: string;
	/** Translated title of this section.  */
	title: string;
	/** Formatted price of this part. */
	price: string | null | undefined;
	bold?: boolean;
}) {
	const { i18n, i18nKeys } = useI18n();
	const { registerFootnote } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;
	const footnoteReference = i18nKey in i18nKeys ? registerFootnote(i18nKey, i18n(i18nKey)) : null;

	return (
		<Layout direction={{ default: 'column', md: 'row' }} columnGap="32px" justify="space-between">
			<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{title}
				{footnoteReference && <FootnoteReferenceNgc reference={footnoteReference} />}
			</Text>
			<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>{price}</Text>
		</Layout>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n } = useI18n();

	return {
		priceDateRemark: `*${i18n('ngc.summary.remark')}`,
	};
}
